import { PLAYBACK_STATE } from 'enums'
import * as Assets from 'models/Asset'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectTimelinePlaybackState } from 'selectors/playback'
import { timelineTimeToSeconds } from 'Util'

type UseAudioContextProps = {
  gain: number
  asset: Assets.AudioAsset | Assets.VideoAsset
  videoRef: RefObject<HTMLVideoElement>
}

function useAudioContext({ gain, asset, videoRef }: UseAudioContextProps) {
  const gainNode = useRef<GainNode | null>(null)
  const audioSourceNode = useRef<AudioNode | null>(null)
  const [ playerCurrentTime, setCurrentTime ] = useState(0)
  const playbackState = useSelector(selectTimelinePlaybackState)

  const isSetFadeIn = useRef(false)
  const isSetFadeOut = useRef(false)

  useEffect(() => {
    const isAudio = asset instanceof Assets.AudioAsset || asset.hasAudio
    const handleTimeUpdate = (e: Event) => {
      const event = e.target as HTMLMediaElement
      setCurrentTime(event.currentTime)
    }

    const seeking = (e: Event) => {
      const event = e.target as HTMLMediaElement
      setCurrentTime(event.currentTime)
      isSetFadeOut.current = false
      isSetFadeIn.current = false
      if (isAudio) {
        if (gainNode.current) {
          if (gainNode.current.gain.value === 0) {
            gainNode.current.gain.value = gain
          }
        }
      }
    }

    const handleEnd = () => {
      setCurrentTime(0)
      isSetFadeOut.current = false
      isSetFadeIn.current = false
      if (isAudio) {
        if (gainNode.current) {
          gainNode.current.gain.value = gain
        }
      }
    }

    const audio = __CFG__.AUDIO_SETTINGS.TRANSITIONS && isAudio && videoRef?.current
    if (audio) {
      audio.addEventListener('timeupdate', handleTimeUpdate)
      audio.addEventListener('seeking', seeking)
      audio.addEventListener('ended', handleEnd)
    }
    return () => {
      if (audio) {
        audio.removeEventListener('timeupdate', handleTimeUpdate)
        audio.removeEventListener('seeking', seeking)
        audio.removeEventListener('ended', handleEnd)
      }
    }
  }, [ asset, videoRef, gain, playbackState ])

  useEffect(() => {
    if (!gainNode.current) return
    gainNode.current.gain.value = gain
  }, [ gain ])

  useEffect(() => () => {
    if (gainNode.current && audioSourceNode.current) {
      gainNode.current.disconnect()
      audioSourceNode.current.disconnect()
      gainNode.current = null
      audioSourceNode.current = null
    }
    isSetFadeOut.current = false
    isSetFadeIn.current = false
  }, [])

  useEffect(() => {
    const applyFadeEffects = () => {
      if (playbackState === PLAYBACK_STATE.PLAY) {
        const isAudio = asset instanceof Assets.AudioAsset || asset.hasAudio
        if (gainNode.current && isAudio) {
          const { currentTime } = gainNode.current.context
          if (!isSetFadeIn.current && asset.fadeInDuration && asset.fadeInIsActive) {
            if (playerCurrentTime < timelineTimeToSeconds(asset.fadeInDuration)) {
              isSetFadeIn.current = true
              gainNode.current.gain.setValueAtTime(0, currentTime)
              gainNode.current.gain.linearRampToValueAtTime(gain,
                currentTime + timelineTimeToSeconds(asset.fadeInDuration))
            }
          }
          if (!isSetFadeOut.current && asset.fadeOutDuration && asset.fadeOutIsActive) {
            const diff = timelineTimeToSeconds(asset.duration - asset.fadeOutDuration)
            if (playerCurrentTime > diff) {
              isSetFadeOut.current = true
              gainNode.current.gain.setValueAtTime(gain, currentTime)
              gainNode.current.gain.linearRampToValueAtTime(0,
                currentTime + timelineTimeToSeconds(asset.duration) - playerCurrentTime)
            }
          }
        }
      }
    }
    const id = __CFG__.AUDIO_SETTINGS.TRANSITIONS ? requestAnimationFrame(applyFadeEffects) : null
    return () => {
      if (id) {
        cancelAnimationFrame(id)
      }
    }
  }, [ gainNode, gain, asset, playerCurrentTime, playbackState ])

  const setGainNode = (node: GainNode) => {
    gainNode.current = node
    const isFadeInAudio = asset.fadeInDuration && asset.fadeInIsActive
    gainNode.current.gain.value = isFadeInAudio ? 0 : Math.max(1, asset.volume)
  }

  const setAudioSourceNode = useCallback((node: AudioNode) => {
    audioSourceNode.current = node
  }, [])

  return [ setGainNode, setAudioSourceNode ]
}

export default useAudioContext
